import type {
  AxiosRequestConfig,
  DefaultQueryError,
} from '@ahm/api-wrappers-core';
import { useMutation, type UseMutationOptions } from '@tanstack/react-query';
import { apiRoutes } from './api-routes';
import { webviewHttpClient } from '@/app/[locale]/(order)/components/auth/webview-http-client';

interface CustomizationItem {
  id: string;
  quantity: number;
}

interface OrderItem {
  id: number;
  quantity: number;
  customizations?: CustomizationItem[];
}

interface Receiver {}

export interface EstimateItemFeeParams {
  receiver: Receiver;
  items: OrderItem[];
  store_id: number;
}

export interface EstimateItemFeeParamsWithToken extends EstimateItemFeeParams {
  token: string;
}

export interface EstimateItemFeeResponse {
  city_id: string;
  items: Item[];
  applied_promo: AppliedPromo[];
  subtotal: number;
  total_payment: number;
  store_id: number;
  receiver: Receiver;
  delivery_discount: number;
  merchant_discount: number;
  platform_discount: number;
  item_discount: number;
  delivery_fee: number;
  platform_fee: number;
  item_fee: number;
  distance: number;
  tracking_link: string;
  delivery_subscription_code: string;
  estimated_total_payment: number;
  branch_id: string;
}

export interface AppliedPromo {
  promo_code: string;
  name: string;
  order_code: string;
  customer_id: string;
  scope: string;
  item_ids: number[];
  store_id: number;
  total_discount_value: number;
  create_at: number;
  discount_config: DiscountConfig;
}

export interface DiscountConfig {
  type: string;
  value: number;
  max_discount_value: number;
  max_apply_per_item: number;
}

export interface Item {
  id: number;
  price: number;
  customization_total_pay: number;
  quantity: number;
  note?: string;
  customizations: Customization[] | null;
  name: string;
}

export interface Customization {
  id: string;
  price: number;
  quantity: number;
  option_name: string;
  name: string;
  note: string;
  is_item: boolean;
}

export const estimateItemFeeWSFn = async (
  params: EstimateItemFeeParamsWithToken,
  config: AxiosRequestConfig = {}
): Promise<EstimateItemFeeResponse> => {
  const { token, ...requestBody } = params;

  const response = await webviewHttpClient.post<EstimateItemFeeResponse>(
    apiRoutes.ahafood.web_view.estimate_ws,
    requestBody,
    {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return response?.data;
};

export const useEstimateItemFeeWS = (
  token: string,
  options?: UseMutationOptions<
    EstimateItemFeeResponse,
    DefaultQueryError,
    EstimateItemFeeParams
  >
) => {
  return useMutation<
    EstimateItemFeeResponse,
    DefaultQueryError,
    EstimateItemFeeParams
  >({
    mutationFn: (params: EstimateItemFeeParams) => {
      return estimateItemFeeWSFn({ ...params, token }, {});
    },
    ...options,
  });
};
